import { comparer, makeAutoObservable, observable, reaction, runInAction } from "mobx";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import type { RootStore } from "./root.store";
import {
  isColumnFiltersState,
  isSortingState,
  readTablePref,
  writeTablePref,
  type TablePrefsKey,
} from "@/components/data-table/table-prefs-storage";
import { tablePrefsEnabledNow } from "@/components/data-table/table-prefs-gate";
import { pollExportStatus } from "@/components/data-table/use-csv-export";
import { runBulkAction, runBulkDelete } from "@/components/data-table/run-bulk-delete";
import { type AnalyticsSource, track } from "@/lib/analytics";
import {
  appendProject,
  cachedProject,
  forgetProject,
  invalidateProjectBanks,
  invalidateProjectDetails,
  patchProject,
  projectCacheFor,
  projectDetailKey,
} from "@/queries/projects";
import {
  cachedProjectsTableRow,
  holdProjectsTableRows,
  invalidateProjectsTable,
} from "@/queries/projects-table";
import { peekQueryClient } from "@/queries/client";
import { projectActivitiesKey } from "@/queries/project-resources";
import { queryCacheScope } from "./query-cache-scope";
import { workItemListsChanged } from "@/queries/work-items";
import type {
  TBulkArchiveProjectsResult,
  TBulkAssignLeadsResult,
  TBulkSetProjectStatusResult,
  TPartialProject,
  TProject,
  TProjectsBoardGroupBy,
  TWorkspaceProjectsListQuery,
} from "@/types/project";
import type {
  TBulkDeleteResult,
  TBulkOperationStatus,
  TBulkSelection,
} from "@/types/bulk-selection";
import type { TProjectArchiveImpact } from "@/services/project.service";
import type { ProjectGroupBy } from "@/components/projects/project-group-rows";
import type { ProjectsListDisplay, ProjectViewFilterState } from "@/helpers/project-view.helper";

const PROJECTS_DEFAULT_PER_PAGE = 50;

/**
 * Pages the Board, Timeline and Calendar layouts fetch on their own before they
 * stop and offer "Load more" (see `useProjectsTable`). Those layouts have no
 * scroll sentinel, so without this they drew the first page and nothing said
 * the rest existed. Ten pages is 500 projects: ten sequential reads, each
 * inside the 1s read budget, so a 500-project workspace settles in a few
 * seconds behind a visible "Loading projects… X of Y" line. A workspace with
 * more than that gets told, and asks for the next 500 itself.
 */
export const PROJECTS_LAYOUT_AUTOLOAD_PAGES = 10;

/**
 * localStorage key an e2e spec sets to shrink the list page size, so a spec can
 * cross a page boundary with a handful of its own projects instead of creating
 * fifty. Nothing in the app writes it. The backend still caps `per_page` at 100.
 */
export const PROJECTS_PAGE_SIZE_OVERRIDE_KEY = "cognota:projects-page-size-override";

function projectsListPageSize(): number {
  if (typeof window === "undefined") return PROJECTS_DEFAULT_PER_PAGE;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(PROJECTS_PAGE_SIZE_OVERRIDE_KEY);
  } catch (err) {
    console.warn("[projects] read page size override failed", err);
    return PROJECTS_DEFAULT_PER_PAGE;
  }
  if (raw === null) return PROJECTS_DEFAULT_PER_PAGE;
  const size = Number(raw);
  return Number.isInteger(size) && size > 0 && size <= PROJECTS_DEFAULT_PER_PAGE
    ? size
    : PROJECTS_DEFAULT_PER_PAGE;
}

/** Must match the `tableId` `routes/workspace/projects.tsx` passes to
 *  `<DataTable>`, so the stored sort/filters share that table's namespace. */
const PROJECTS_TABLE_ID = "projects";

/**
 * Column-filter ids the LIST ENDPOINT does not understand — the projects route
 * narrows rows for these in the browser.
 *
 * It lives here, next to `setColumnFilters`, because the store now owns the
 * whole filter set: it persists both halves under one key and hydrates both
 * before the first fetch. Splitting the rule across the route and the store
 * would let the stored value and the applied value disagree with nothing to
 * catch it.
 *
 * These are deliberately NOT capturable into a saved view (NG-529): they narrow
 * the page already in memory, so the list endpoint never saw them. Persisting
 * one into a view would restore a row set the server cannot reproduce — the
 * first page would look filtered and every page after it would not. A capture
 * helper (`projectsFilterState`) must skip any filter id in this set.
 */
const CLIENT_SIDE_FILTER_IDS: ReadonlySet<string> = new Set(["name", "progress", "openTasks"]);

/** The column-filter shapes a custom-field column can carry (see
 *  `column-filter.tsx`): "multi" for select/multiselect, "dateRange" for date. */
export type TCustomFieldColumnFilter =
  | { type: "multi"; value: string[] }
  | { type: "dateRange"; value: { from: string; to: string } };

function isActiveCustomFieldFilter(filter: TCustomFieldColumnFilter): boolean {
  if (filter.type === "multi") return filter.value.length > 0;
  return Boolean(filter.value.from || filter.value.to);
}

/** Serialize active custom-field column filters to the backend's repeatable
 *  `custom_field` values: `<field_id>:<value>` per picked option, and
 *  `<field_id>:gte:<from>` / `<field_id>:lte:<to>` for date bounds. */
function serializeCustomFieldFilters(
  entries: Iterable<[columnId: string, filter: TCustomFieldColumnFilter]>,
): string[] {
  const out: string[] = [];
  for (const [columnId, filter] of entries) {
    const fieldId = columnId.replace(/^cf:/, "");
    if (filter.type === "multi") {
      for (const value of filter.value) out.push(`${fieldId}:${value}`);
    } else {
      if (filter.value.from) out.push(`${fieldId}:gte:${filter.value.from}`);
      if (filter.value.to) out.push(`${fieldId}:lte:${filter.value.to}`);
    }
  }
  return out;
}

type ProjectRow = TProject | TPartialProject;

/**
 * The keys of a project write whose value differs from the row before it:
 * `project_updated`'s `fields`. A row the cache does not hold counts every key
 * it sends. Values compare by their JSON form, which covers the scalars, id
 * lists and small objects a project PATCH carries.
 */
function changedProjectFields(before: ProjectRow | undefined, data: Partial<TProject>): string[] {
  return (Object.keys(data) as (keyof TProject)[]).filter(
    (key) =>
      !before ||
      JSON.stringify((before as Partial<TProject>)[key] ?? null) !==
        JSON.stringify(data[key] ?? null),
  );
}

/**
 * Product analytics for one successful project write (NG-2055). `project_updated`
 * fires for every write that changed something, and a status or lead change
 * also fires its own event. A one-for-one lead swap is a single
 * `project_lead_assigned` carrying `from_member_id`. Props are ids only.
 */
function trackProjectWrite(
  projectId: string,
  before: ProjectRow | undefined,
  data: Partial<TProject>,
  source: AnalyticsSource,
): void {
  const fields = changedProjectFields(before, data);
  if (fields.length === 0) return;
  track("project_updated", { project_id: projectId, fields, source });
  if (fields.includes("state")) {
    track("project_status_changed", {
      project_id: projectId,
      from: before?.state ?? null,
      to: data.state ?? null,
      source,
    });
  }
  if (fields.includes("project_leads")) {
    const previous = before?.project_leads ?? [];
    const next = data.project_leads ?? [];
    const added = next.filter((id) => !previous.includes(id));
    const removed = previous.filter((id) => !next.includes(id));
    if (added.length === 1 && removed.length === 1) {
      track("project_lead_assigned", {
        project_id: projectId,
        member_id: added[0],
        from_member_id: removed[0],
        source,
      });
      return;
    }
    for (const memberId of added) {
      track("project_lead_assigned", { project_id: projectId, member_id: memberId, source });
    }
    for (const memberId of removed) {
      track("project_lead_unassigned", { project_id: projectId, member_id: memberId, source });
    }
  }
}

/** How many projects a bulk action changed, whichever result shape came back. */
function bulkChangedCount(result: object): number {
  const r = result as Record<string, unknown>;
  const count = r.deleted ?? r.archived ?? r.updated ?? r.processed;
  return typeof count === "number" ? count : 0;
}

export class ProjectStore {
  searchQuery = "";
  stateFilter = observable.array<string>([], { deep: false });
  // Managed Project Category value ids (NG-420). Single-valued FK server-side,
  // so a multi-pick is an OR over ids — same shape as `stateFilter`.
  categoryFilter = observable.array<string>([], { deep: false });
  leadFilter = observable.array<string>([], { deep: false });
  deliveryTeamsFilter = observable.array<string>([], { deep: false });
  // Home team ids (NG-2163). An OR over ids matched against each project's
  // scalar `home_team`, never its roster: a delivery team that is not the Home
  // does not match. Only the flag-gated Home team column and facet set it.
  homeTeamFilter = observable.array<string>([], { deep: false });
  clientTeamsFilter = observable.array<string>([], { deep: false });
  programsFilter = observable.array<string>([], { deep: false });
  // Linked-goal ids (NG-619). Same OR-over-ids shape as `programsFilter`; the
  // server joins the ACTIVE link rows, so a goal a project was unlinked from
  // stops matching.
  goalsFilter = observable.array<string>([], { deep: false });
  // Whether `goalsFilter` may reach the request and the chips (NG-2844). The
  // list sets it from the `goals` flag, so the store reads no flag itself.
  // While false the stored ids stay put: a saved view or the persisted prefs
  // keep them, and they apply again once goals turn on. Starts false so nothing
  // that loads before the list has answered sends a filter the user cannot see.
  goalsFilterEnabled = false;
  // Whether the `project-last-activity` flag is on (NG-3767). While false the
  // stored `sorting` preference is kept but a persisted `lastActivity` sort is NOT
  // serialized to `order_by`, so a user who sorted by it while the flag was on does
  // not keep the list invisibly ordered by a dark feature after rollback. Starts
  // false so a load before the list has answered never sends the dark token.
  lastActivitySortEnabled = false;
  // Process ids, and `ProcessStage` ids matched against each project's DERIVED
  // `current_stage` (NG-713). Same OR-over-ids shape as `stateFilter`.
  processFilter = observable.array<string>([], { deep: false });
  stageFilter = observable.array<string>([], { deep: false });
  archivedFilter = false;
  // Active custom-field column filters, keyed by column id ("cf:<field_id>").
  // Values are the raw column-filter shapes ("multi" for select/multiselect,
  // "dateRange" for date) — serialized to the backend's repeatable
  // `custom_field=<field_id>:<value>` wire format at query-build time.
  customFieldFilters = observable.map<string, TCustomFieldColumnFilter>();
  /**
   * The client-side filters (`CLIENT_SIDE_FILTER_IDS`), held verbatim as full
   * entries — the route's row predicate switches on `id`, so bare values would
   * not tell it which filter is which. Never sent to the API; the store holds
   * them so ONE owner persists the whole set the user sees.
   */
  clientColumnFilters = observable.array<ColumnFiltersState[number]>([], { deep: false });

  /** Board display prefs (NG-713). NOT list filters: the board bins the loaded
   *  rows client-side, so neither reaches the server and neither belongs in
   *  `clearListFilters`'s invariant (which covers what `buildProjectsListQuery`
   *  sends). Held here rather than in component state so a layout switch and a
   *  remount keep the pick, exactly like `sorting`. */
  boardGroupBy: TProjectsBoardGroupBy = "status";
  boardProcessId: string | null = null;

  sorting: SortingState = [];
  // Server-side group-by token; "none" disables grouping. Single-valued
  // dimensions ("status", "category") group one row per project; the M2M ones
  // duplicate a project into each of its groups (#1437).
  groupBy: ProjectGroupBy = "none";

  // The workspace whose stored list prefs are currently loaded, or null.
  //
  // A single slug rather than a set of every slug seen: the state it guards
  // (`sorting`, the filter observables) is GLOBAL to the store, so
  // once-per-slug-per-session would let `/acme` → `/other` → `/acme` leave
  // acme's list running on other's workspace-local entity ids — an empty table
  // with chips pointing at rows that do not exist. Comparing against the
  // current slug keeps same-slug hydration idempotent while making a return
  // visit re-read the returning workspace's key.
  //
  // Deliberately not observable: bookkeeping about a read, nothing renders it.
  private hydratedSlug: string | null = null;
  // True only while `hydrateListPrefs` is applying a value it just READ, so the
  // persistence reactions below don't write it straight back.
  private hydratingListPrefs = false;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        hydratedSlug: false,
        hydratingListPrefs: false,
        // NOT an action, deliberately — see `hydrateListPrefs`. MobX flushes
        // pending reactions when the OUTERMOST action ends, so if this method
        // were an action its own `runInAction` would not flush them and the
        // write-back guard inside it would already have been cleared by the
        // time the persistence reactions ran.
        hydrateListPrefs: false,
      } as never,
      { autoBind: true },
    );
    // Opting out of the action wrapping above also opts out of `autoBind`, and
    // callers here routinely lift store methods off the instance. Bind it back so
    // that stays safe.
    this.hydrateListPrefs = this.hydrateListPrefs.bind(this);
    this.watchListPrefs();
  }

  /**
   * The ONE place the list sort + filters are persisted.
   *
   * Deliberately a reaction over the DERIVED state rather than a write in each
   * setter. Many mutators write the state these two keys hold —
   * `setColumnFilters`, `setSearchQuery` and every facet setter — and wiring
   * the write to just one of them is exactly how storage came to diverge from
   * state: clearing the search box left the stored `search` entry behind, so
   * a reload resurrected it. Reacting to the derived value covers every
   * mutator, including the next one someone adds.
   *
   * The filter expression is the SAME union the route feeds to `<DataTable>` —
   * server-derived entries plus the client half — so what gets stored is the
   * whole set the user sees, never a truncated server half.
   *
   * No disposer: `ProjectStore` lives for the lifetime of the app, so these
   * reactions are never torn down.
   */
  private watchListPrefs(): void {
    reaction(
      () => [...this.columnFilters, ...this.clientColumnFilters],
      (filters) => this.persistListPref("columnFilters", filters),
      // Structural, so re-applying an equal set is not a write.
      { equals: comparer.structural },
    );
    reaction(
      () => [...this.sorting],
      (sorting) => this.persistListPref("sorting", sorting),
      { equals: comparer.structural },
    );
  }

  /**
   * The current list filters/sort/group-by, in the same shape the list
   * endpoint and CSV export already scope to (`buildProjectsListQuery`) —
   * exposed publicly so a "select all N matching" bulk selection (mode
   * `"all"`) can reuse it as `selection.filters` without duplicating the
   * filter-serialization logic.
   */
  get currentListQuery(): TWorkspaceProjectsListQuery {
    return this.buildProjectsListQuery(null);
  }

  setSearchQuery(value: string): void {
    this.searchQuery = value;
  }
  setStateFilter(ids: Iterable<string>): void {
    this.stateFilter.replace([...ids]);
  }
  setCategoryFilter(ids: Iterable<string>): void {
    this.categoryFilter.replace([...ids]);
  }
  setLeadFilter(ids: Iterable<string>): void {
    this.leadFilter.replace([...ids]);
  }
  setDeliveryTeamsFilter(ids: Iterable<string>): void {
    this.deliveryTeamsFilter.replace([...ids]);
  }
  setHomeTeamFilter(ids: Iterable<string>): void {
    this.homeTeamFilter.replace([...ids]);
  }
  setClientTeamsFilter(ids: Iterable<string>): void {
    this.clientTeamsFilter.replace([...ids]);
  }
  setProgramsFilter(ids: Iterable<string>): void {
    this.programsFilter.replace([...ids]);
  }
  setGoalsFilter(ids: Iterable<string>): void {
    this.goalsFilter.replace([...ids]);
  }
  setGoalsFilterEnabled(enabled: boolean): void {
    this.goalsFilterEnabled = enabled;
  }
  setLastActivitySortEnabled(enabled: boolean): void {
    this.lastActivitySortEnabled = enabled;
  }
  setProcessFilter(ids: Iterable<string>): void {
    this.processFilter.replace([...ids]);
  }
  setStageFilter(ids: Iterable<string>): void {
    this.stageFilter.replace([...ids]);
  }
  setBoardGroupBy(next: TProjectsBoardGroupBy): void {
    this.boardGroupBy = next;
  }
  setBoardProcessId(next: string | null): void {
    this.boardProcessId = next;
  }
  setArchivedFilter(value: boolean): void {
    this.archivedFilter = value;
  }
  /** Apply both Display settings in one action. The list's load effect is keyed
   *  on both, so a change to either reloads once, and an unchanged pair writes
   *  nothing a reaction can see. */
  setListDisplay(display: ProjectsListDisplay): void {
    this.groupBy = display.groupBy;
    this.archivedFilter = display.archived;
  }
  /**
   * The current projects filter set, in the shape a saved view stores.
   *
   * The read counterpart of `hydrateListFilters`; both live here so the field list
   * stays in one file rather than one copy per consumer.
   */
  get projectsFilterState(): ProjectViewFilterState {
    return {
      search: this.searchQuery,
      stateIds: [...this.stateFilter],
      categoryIds: [...this.categoryFilter],
      leadIds: [...this.leadFilter],
      deliveryTeamIds: [...this.deliveryTeamsFilter],
      homeTeamIds: [...this.homeTeamFilter],
      clientTeamIds: [...this.clientTeamsFilter],
      programIds: [...this.programsFilter],
      goalIds: [...this.goalsFilter],
      processIds: [...this.processFilter],
      stageIds: [...this.stageFilter],
      archived: this.archivedFilter,
      groupBy: this.groupBy,
    };
  }

  /**
   * REPLACE the whole projects filter set with a saved view's state.
   *
   * A full replace, not a merge, and that is the point: this store is a singleton
   * shared by the plain Projects list and every project-view page, so a merge would
   * let one surface's filters survive into the next. Clearing first is what makes
   * it a replace, and it lives here rather than at the call site so every caller
   * gets it — the same ownership `IntakeStore.hydrateSubmissionsFilters` has.
   *
   * INVARIANT: keep this covering every field `clearListFilters` resets — a field
   * cleared there but not assigned here silently drops that filter when a view
   * opens, and one assigned here but not cleared there leaks into the next surface.
   *
   * Does NOT fetch — the caller decides when to load, so a mount can seed and load
   * once rather than twice.
   */
  hydrateListFilters(next: ProjectViewFilterState): void {
    // Dropping `hydratedSlug` is what keeps a view's filters out of the plain
    // list's persisted prefs (NG-517 review). The persistence reactions flush
    // when the OUTERMOST action ends and read the slug then — so nulling it
    // here means this seed, and every filter/sort change made while the view
    // owns the store, has no key to write under. The next plain-list mount
    // re-arms persistence via `hydrateListPrefs`, whose same-slug guard the
    // null also releases, so it re-reads the stored set this method displaced.
    this.hydratedSlug = null;
    this.clearListFilters();
    this.searchQuery = next.search;
    this.stateFilter.replace(next.stateIds);
    this.categoryFilter.replace(next.categoryIds);
    this.leadFilter.replace(next.leadIds);
    this.deliveryTeamsFilter.replace(next.deliveryTeamIds);
    this.homeTeamFilter.replace(next.homeTeamIds);
    this.clientTeamsFilter.replace(next.clientTeamIds);
    this.programsFilter.replace(next.programIds);
    this.goalsFilter.replace(next.goalIds);
    this.processFilter.replace(next.processIds);
    this.stageFilter.replace(next.stageIds);
    this.archivedFilter = next.archived;
    this.groupBy = next.groupBy;
  }

  /**
   * Reset every list filter this store holds.
   *
   * INVARIANT: keep this covering every field `hydrateListFilters` assigns, and
   * every field `buildProjectsListQuery` sends. `groupBy` and `customFieldFilters`
   * are both in that set: they reach the server (`group_by`, `custom_field`), so
   * leaving them set here is one surface's narrowing surviving into the next.
   */
  clearListFilters(): void {
    this.searchQuery = "";
    this.clientColumnFilters.clear();
    this.stateFilter.clear();
    this.categoryFilter.clear();
    this.leadFilter.clear();
    this.deliveryTeamsFilter.clear();
    this.homeTeamFilter.clear();
    this.clientTeamsFilter.clear();
    this.programsFilter.clear();
    this.goalsFilter.clear();
    this.processFilter.clear();
    this.stageFilter.clear();
    this.archivedFilter = false;
    this.groupBy = "none";
    this.customFieldFilters.clear();
  }

  setSorting(next: SortingState): void {
    this.sorting = next;
    // Persistence is handled by `watchListPrefs`, not here — see why there.
    // No reload here: the sort is part of the list's read key
    // (`useProjectsTable`), so the change itself reads page 1 (NG-2804).
  }

  /** No reload here: the list's load effect is keyed on `groupBy`, as it is on
   *  `archivedFilter`, so a reload from here too would load the page twice. */
  setGroupBy(next: ProjectGroupBy): void {
    this.groupBy = next;
  }

  get columnFilters(): ColumnFiltersState {
    const out: ColumnFiltersState = [];
    if (this.searchQuery.trim()) {
      out.push({ id: "search", value: { type: "text", value: this.searchQuery } });
    }
    if (this.stateFilter.length > 0) {
      // id must match the "status" column id in projects-columns.tsx — the
      // primitive keys filters/chips by column id, not the BE field name.
      out.push({ id: "status", value: { type: "multi", value: [...this.stateFilter] } });
    }
    if (this.categoryFilter.length > 0) {
      out.push({ id: "category", value: { type: "multi", value: [...this.categoryFilter] } });
    }
    if (this.leadFilter.length > 0) {
      out.push({ id: "lead", value: { type: "multi", value: [...this.leadFilter] } });
    }
    if (this.deliveryTeamsFilter.length > 0) {
      out.push({
        id: "deliveryTeams",
        value: { type: "multi", value: [...this.deliveryTeamsFilter] },
      });
    }
    if (this.homeTeamFilter.length > 0) {
      out.push({ id: "homeTeam", value: { type: "multi", value: [...this.homeTeamFilter] } });
    }
    if (this.clientTeamsFilter.length > 0) {
      out.push({ id: "clientTeams", value: { type: "multi", value: [...this.clientTeamsFilter] } });
    }
    if (this.programsFilter.length > 0) {
      out.push({ id: "programs", value: { type: "multi", value: [...this.programsFilter] } });
    }
    if (this.goalsFilter.length > 0) {
      out.push({ id: "goals", value: { type: "multi", value: [...this.goalsFilter] } });
    }
    // Only the stage entry: there IS a "stage" column (NG-713), and there is no
    // `process` column — emitting an id the table has no column for would let
    // the table's own `onColumnFiltersChange` drop it on every list render.
    if (this.stageFilter.length > 0) {
      out.push({ id: "stage", value: { type: "multi", value: [...this.stageFilter] } });
    }
    // Not `archivedFilter`: Show archived is a Display setting, which the plain
    // list keeps in the URL. As a filter entry it also showed as a chip named
    // `archived` whose dismiss changed the setting behind the URL's back, and
    // rode the persisted filters as a second memory of the same choice.
    for (const [id, value] of this.customFieldFilters) {
      out.push({ id, value });
    }
    return out;
  }

  /**
   * The server-side filter entries the user can see, which is what the table
   * and its chips take. `columnFilters` stays the whole set, because that is
   * what gets persisted: dropping the goals entry there would delete it from
   * storage while goals are dark (NG-2844).
   */
  get visibleColumnFilters(): ColumnFiltersState {
    if (this.goalsFilterEnabled) return this.columnFilters;
    return this.columnFilters.filter((f) => f.id !== "goals");
  }

  private listPrefsKey(slug: string): TablePrefsKey {
    // `|| null` is a second line of defence — `persistListPref` and
    // `hydrateListPrefs` both bail on a falsy slug, so this should be
    // unreachable. An empty-string scope collapses to the UNSCOPED key, which
    // both collides with the legacy column-prefs namespace and orphans the
    // value the moment a real slug arrives.
    return { tableId: PROJECTS_TABLE_ID, scope: slug || null };
  }

  private persistListPref(
    slice: "sorting" | "columnFilters",
    value: SortingState | ColumnFiltersState,
  ): void {
    // Behind `persisted-table-prefs` (NG-517). This store reaches
    // `writeTablePref` directly rather than through `useTableSorting` /
    // `useTableColumnFilters`, so the hook-side gate does not cover it — without
    // this check the three busiest lists would keep persisting with the flag off.
    if (!tablePrefsEnabledNow(this.rootStore.featureFlags)) return;
    // Hydration applies a value it just READ; writing it back would be a
    // pointless write and would misreport who last wrote the key.
    if (this.hydratingListPrefs) return;
    // The slug hydration READ this state under — deliberately not
    // `rootStore.router.workspaceSlug`, which `RouterSync` sets from an effect
    // (`router-sync.tsx`), so during a `clientLoader` it still holds the
    // OUTGOING workspace. Reading it here made correctness depend on `RouterSync`
    // rendering before `<Outlet />` in `root.tsx` — reorder those siblings, or
    // put `RouterSync` under a suspense boundary, and one workspace's filters
    // start filing under another's key. `hydrateListPrefs` runs before any
    // mutator can, and it takes its slug from the route's own params, which
    // cannot lag. Matches `intake.store.ts` and `report.store.ts`.
    const slug = this.hydratedSlug;
    // No workspace means no key worth writing: an empty-string scope collapses
    // to the UNSCOPED bucket, which would be orphaned once a real slug arrives.
    if (!slug) return;
    writeTablePref(this.listPrefsKey(slug), slice, value);
  }

  /**
   * Read this workspace's stored sort + filters into the store.
   *
   * Called from the route's `clientLoader`, which React Router runs BEFORE the
   * first render — so the load effect's first run already builds a query
   * carrying them and the page issues ONE request instead of an unfiltered one
   * followed by a filtered one. Hydrating from an effect cannot work here: that
   * effect is keyed on signatures of the very observables hydration writes, so
   * it would re-fire itself.
   *
   * Idempotent while the slug does not change, so a re-entry into the same
   * workspace cannot replay stale storage over a change the user made since.
   * A DIFFERENT slug always re-hydrates — see `hydratedSlug` for why that is
   * not optional.
   */
  hydrateListPrefs(slug: string): void {
    if (!slug || this.hydratedSlug === slug) return;
    this.hydratedSlug = slug;
    const key = this.listPrefsKey(slug);
    // Behind `persisted-table-prefs` (NG-517). Gated at the READS rather than
    // with an early return, because `applyColumnFilters` below REPLACES the
    // whole filter set — so applying the empty arrays is what resets a store
    // still holding another workspace's filters, or a saved view's. An early
    // return skipped that reset, and the plain Projects list then queried with
    // filters the user could not see and had not chosen (review, NG-517).
    // Restoring nothing is exactly the pre-NG-517 behaviour: `sorting` starts
    // `[]` on this store, so the empty arrays are the values the reset applied
    // before any of this existed.
    const restore = tablePrefsEnabledNow(this.rootStore.featureFlags);
    // `readTablePref` distinguishes absent from empty, so a user who cleared
    // the sort gets `[]` back rather than a default.
    const sorting = restore ? readTablePref<SortingState>(key, "sorting", [], isSortingState) : [];
    const filters = restore
      ? readTablePref<ColumnFiltersState>(key, "columnFilters", [], isColumnFiltersState)
      : [];
    // The guard has to still be set when the persistence reactions run. MobX
    // flushes them as the OUTERMOST action ends — which is why this method is
    // excluded from `makeAutoObservable`'s action wrapping (see the constructor):
    // that makes the `runInAction` below the outermost action, so the reactions
    // fire inside it, while the guard is up. All observable writes here must
    // therefore stay inside that `runInAction`.
    this.hydratingListPrefs = true;
    try {
      runInAction(() => {
        this.sorting = sorting;
        this.applyColumnFilters(filters);
      });
    } finally {
      this.hydratingListPrefs = false;
    }
  }

  setColumnFilters(next: ColumnFiltersState): void {
    // Persistence is handled by `watchListPrefs`, not here — see why there.
    // While goals are dark the table is handed `visibleColumnFilters`, so the
    // set it hands back never carries the goals entry. Applying it as is would
    // clear a goal filter the user cannot see, and persist that (NG-2844).
    const keepHiddenGoals = !this.goalsFilterEnabled && this.goalsFilter.length > 0;
    this.applyColumnFilters(
      keepHiddenGoals
        ? [
            ...next.filter((f) => f.id !== "goals"),
            { id: "goals", value: { type: "multi", value: [...this.goalsFilter] } },
          ]
        : next,
    );
  }

  /** Apply a filter set to the observables. No persistence and no reload, so
   *  hydration can reuse it without writing back what it just read. */
  private applyColumnFilters(next: ColumnFiltersState): void {
    const find = (id: string) =>
      next.find((f) => f.id === id)?.value as
        | { type: "text"; value: string }
        | { type: "multi"; value: string[] }
        | undefined;

    this.searchQuery = (find("search") as { value: string } | undefined)?.value ?? "";
    this.stateFilter.replace((find("status") as { value: string[] } | undefined)?.value ?? []);
    this.categoryFilter.replace((find("category") as { value: string[] } | undefined)?.value ?? []);
    this.leadFilter.replace((find("lead") as { value: string[] } | undefined)?.value ?? []);
    this.deliveryTeamsFilter.replace(
      (find("deliveryTeams") as { value: string[] } | undefined)?.value ?? [],
    );
    // The Home team COLUMN header filter (NG-2163), the same door the Goals line
    // below documents: without it the funnel ticks and narrows nothing.
    this.homeTeamFilter.replace((find("homeTeam") as { value: string[] } | undefined)?.value ?? []);
    this.clientTeamsFilter.replace(
      (find("clientTeams") as { value: string[] } | undefined)?.value ?? [],
    );
    this.programsFilter.replace((find("programs") as { value: string[] } | undefined)?.value ?? []);
    // The Goals COLUMN header filter (NG-619). Without this line the column's
    // funnel opens, ticks and highlights, and narrows nothing — the header
    // filter and the filter facet are two separate doors into the same store
    // field, and only the facet was wired.
    this.goalsFilter.replace((find("goals") as { value: string[] } | undefined)?.value ?? []);
    this.stageFilter.replace((find("stage") as { value: string[] } | undefined)?.value ?? []);
    // An `archived` entry stored before Show archived left the filter set is
    // ignored: the setting belongs to the Display menu now (see `columnFilters`).
    this.customFieldFilters.replace(
      next
        .filter((f) => f.id.startsWith("cf:"))
        .map((f) => [f.id, f.value as TCustomFieldColumnFilter] as const)
        .filter(([, value]) => isActiveCustomFieldFilter(value)),
    );
    this.clientColumnFilters.replace(next.filter((f) => CLIENT_SIDE_FILTER_IDS.has(f.id)));
  }

  private buildProjectsListQuery(cursor: string | null): TWorkspaceProjectsListQuery {
    const query: TWorkspaceProjectsListQuery = {
      per_page: projectsListPageSize(),
    };
    const trimmed = this.searchQuery.trim();
    if (trimmed) query.q = trimmed;
    if (this.stateFilter.length > 0) query.state = [...this.stateFilter];
    if (this.categoryFilter.length > 0) query.category = [...this.categoryFilter];
    if (this.leadFilter.length > 0) query.lead = [...this.leadFilter];
    if (this.deliveryTeamsFilter.length > 0) query.delivery_teams = [...this.deliveryTeamsFilter];
    if (this.homeTeamFilter.length > 0) query.home_team = [...this.homeTeamFilter];
    if (this.clientTeamsFilter.length > 0) query.client_teams = [...this.clientTeamsFilter];
    if (this.programsFilter.length > 0) query.programs = [...this.programsFilter];
    if (this.goalsFilterEnabled && this.goalsFilter.length > 0) query.goals = [...this.goalsFilter];
    if (this.processFilter.length > 0) query.process = [...this.processFilter];
    if (this.stageFilter.length > 0) query.stage = [...this.stageFilter];
    if (this.archivedFilter) query.archived = true;
    const customField = serializeCustomFieldFilters(this.customFieldFilters.entries());
    if (customField.length > 0) query.custom_field = customField;
    // Single-column server-side sort: map the TanStack sort entry to the
    // backend's whitelisted token (column id, prefixed with "-" for desc).
    const sort = this.sorting[0];
    // Don't serialize a `lastActivity` sort while its flag is off (NG-3767): the
    // backend honours the token unflagged, so a persisted pick from when the flag
    // was on would otherwise keep the list invisibly ordered by a dark feature.
    // The `sorting` preference itself is preserved for when the flag returns.
    const sortGated = sort?.id === "lastActivity" && !this.lastActivitySortEnabled;
    if (sort && !sortGated) query.order_by = `${sort.desc ? "-" : ""}${sort.id}`;
    // Group-by orders rows so a group's members stay contiguous across pages;
    // it takes precedence over `order_by` on the server.
    if (this.groupBy !== "none") query.group_by = this.groupBy;
    if (cursor) query.cursor = cursor;
    return query;
  }

  /**
   * Export the projects list as CSV. Scoped to the current filters/sort;
   * ``ids`` (selected rows) restrict the export when present. Returns the raw
   * Response so the caller can stream the file (200) or poll the token (202).
   */
  exportProjectsCsv(slug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.projectService.exportProjects(
      slug,
      body,
      this.buildProjectsListQuery(null),
    );
  }

  /** Poll an async export by token until its presigned URL is ready (or null). */
  pollExport(slug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.projectService.getExportStatus(slug, tok),
    );
  }

  /** Rows one `PROJECTS_LAYOUT_AUTOLOAD_PAGES` batch holds at the current page size. */
  get layoutBatchRows(): number {
    return PROJECTS_LAYOUT_AUTOLOAD_PAGES * projectsListPageSize();
  }

  /**
   * Every write below keeps the query cache in step with it: a project's detail
   * (R1), the workspace's project lists (R2) and the Projects table's pages
   * (NG-2804) live only there (NG-2794). Each takes the `source` of the
   * surface that made it, for its analytics event. It defaults to `page`, the project's own page; any other
   * surface passes its own. Each event fires after the write succeeds, never on
   * failure.
   */
  async createProject(
    slug: string,
    data: Partial<TProject>,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    const payload: Partial<TProject> = {
      ...data,
      module_view: true,
      cycle_view: false,
      issue_views_view: true,
      page_view: true,
      inbox_view: false,
    };

    const project = await this.rootStore.projectService.createProject(slug, payload);
    const cache = projectCacheFor(slug);
    if (cache) appendProject(cache.client, cache.scope, project);
    track("project_created", { project_id: project.id, source });
    return project;
  }

  /**
   * After a write that adds a line to the project's activity feed (an edit, an
   * archive, a restore): mark the cached feed stale (R3, contract §3.1), so an
   * open activity sheet re-reads it and a closed one re-reads on open.
   */
  private refreshActivityAfterWrite(slug: string, projectId: string): void {
    const scope = queryCacheScope(slug, "ProjectStore");
    if (!scope) return;
    peekQueryClient()
      ?.invalidateQueries({ queryKey: projectActivitiesKey(scope, projectId), exact: true })
      .catch((error: unknown) => {
        console.warn("[ProjectStore] marking the activity feed stale failed", {
          slug,
          projectId,
          error,
        });
      });
  }

  async updateProject(
    slug: string,
    projectId: string,
    data: Partial<TProject>,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    const cache = projectCacheFor(slug);
    // Read before the write replaces it: `fields` is what this write changed.
    const before = cache ? cachedProject(cache.client, cache.scope, projectId) : undefined;
    const updated = await this.rootStore.projectService.updateProject(slug, projectId, data);
    // The answer is the whole detail, so it fills an uncached one too.
    if (cache) patchProject(cache.client, cache.scope, updated.id, updated, { seed: true });
    trackProjectWrite(projectId, before, data, source);
    this.refreshActivityAfterWrite(slug, projectId);
    return updated;
  }

  /**
   * Pin a project's stage (NG-3867): freeze `current_stage` to `stageId`,
   * overriding work-item derivation until reverted. NOT optimistic — the server
   * both validates the stage against the project's process and returns the
   * refreshed project (with `is_stage_pinned` / `current_stage` / the pin
   * metadata), which is applied whole, so the row cannot show a pin the backend
   * rejected. Mirrors `updateProject`'s cache seeding.
   */
  async pinProjectStage(
    slug: string,
    projectId: string,
    stageId: string,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    const updated = await this.rootStore.projectService.pinProjectStage(slug, projectId, stageId);
    const cache = projectCacheFor(slug);
    if (cache) patchProject(cache.client, cache.scope, updated.id, updated, { seed: true });
    track("project_stage_pinned", { project_id: projectId, stage_id: stageId, source });
    this.refreshActivityAfterWrite(slug, projectId);
    return updated;
  }

  /**
   * Clear a project's manual stage pin (NG-3867) so `current_stage` tracks
   * work-item completion again. Not optimistic: the recomputed stage is
   * unknowable client-side (it depends on the project's work items), so the
   * server's freshly-recomputed project is applied whole rather than guessed.
   */
  async clearProjectStageOverride(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    const updated = await this.rootStore.projectService.clearProjectStageOverride(slug, projectId);
    const cache = projectCacheFor(slug);
    if (cache) patchProject(cache.client, cache.scope, updated.id, updated, { seed: true });
    track("project_stage_pin_cleared", { project_id: projectId, source });
    this.refreshActivityAfterWrite(slug, projectId);
    return updated;
  }

  /**
   * PATCH a project with optimistic UI updates. The cached detail and list
   * rows, and the table's row, take the new value before the request fires so
   * every reader (e.g. the team projects table) re-renders at once. On failure
   * the prior values are restored and the error is re-thrown so the caller can
   * toast.
   */
  async updateProjectOptimistic(
    slug: string,
    projectId: string,
    data: Partial<TProject>,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    const cache = projectCacheFor(slug);
    const detailKey = cache ? projectDetailKey(cache.scope, projectId) : null;
    const prevDetail =
      cache && detailKey ? cache.client.getQueryData<TProject>(detailKey) : undefined;
    const prevCached = cache ? cachedProject(cache.client, cache.scope, projectId) : undefined;
    // The analytics baseline: the cached detail or list row, else the Projects
    // table's row, for a project only that table has read.
    const baseline: ProjectRow | undefined =
      prevCached ??
      (cache ? cachedProjectsTableRow(cache.client, cache.scope, projectId) : undefined);

    // The table's rows as they are, for the rollback: the patch below edits them too.
    const restoreTableRows = cache
      ? holdProjectsTableRows(cache.client, cache.scope, projectId)
      : null;
    if (cache) patchProject(cache.client, cache.scope, projectId, data);

    try {
      const updated = await this.rootStore.projectService.updateProject(slug, projectId, data);
      // Cancels a read that started before this PATCH, so it cannot land on
      // top of the result. The answer is the whole detail, so it fills an
      // uncached one too: a list overlaying cached details shows the edit.
      if (cache) patchProject(cache.client, cache.scope, projectId, updated, { seed: true });
      trackProjectWrite(projectId, baseline, data, source);
      this.refreshActivityAfterWrite(slug, projectId);
      return updated;
    } catch (error) {
      // Roll the cache, the table's rows included, back to the pre-mutation values.
      if (cache && detailKey) {
        if (prevDetail) cache.client.setQueryData(detailKey, prevDetail);
        else void cache.client.invalidateQueries({ queryKey: detailKey, exact: true });
        if (prevCached) {
          patchProject(cache.client, cache.scope, projectId, prevCached as Partial<TProject>);
        }
      }
      // After the patch above, which reaches the table's rows too: the held
      // rows are the table's own, group and counts included.
      restoreTableRows?.();
      throw error;
    }
  }

  /**
   * After an archive or a restore: the project's detail and rows take the new
   * `archived_at`, and both lists are read again, since the project moved
   * from one to the other.
   */
  private applyArchivedAt(slug: string, projectId: string, archivedAt: string | null): void {
    const cache = projectCacheFor(slug);
    if (cache) {
      patchProject(cache.client, cache.scope, projectId, { archived_at: archivedAt });
      void invalidateProjectBanks(cache.client, cache.scope);
      // The table lists active or archived projects, so the row has moved out.
      void invalidateProjectsTable(cache.client, cache.scope);
    }
  }

  async unarchiveProject(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.projectService.unarchiveProject(slug, projectId);
    track("project_restored", { project_id: projectId, source });
    this.refreshActivityAfterWrite(slug, projectId);
    this.applyArchivedAt(slug, projectId, null);
    // A restore can bring the project's work items back with it (NG-3531).
    workItemListsChanged(slug);
  }

  /** The archive and restore confirmations' counts (NG-3531). Read once per click, not cached. */
  fetchArchiveImpact(slug: string, projectId: string): Promise<TProjectArchiveImpact> {
    return this.rootStore.projectService.getArchiveImpact(slug, projectId);
  }

  /** How many work items a bulk archive of `selection` would also archive (NG-3531). */
  async previewBulkArchiveProjects(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
  ): Promise<number> {
    const preview = await this.rootStore.projectService.previewBulkArchiveProjects(slug, selection);
    return preview.work_item_count;
  }

  async archiveProject(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const response = await this.rootStore.projectService.archiveProject(slug, projectId);
    track("project_archived", { project_id: projectId, source });
    this.refreshActivityAfterWrite(slug, projectId);
    this.applyArchivedAt(slug, projectId, response.archived_at ?? new Date().toISOString());
    // An archive can take the project's work items with it (NG-3531): re-read the
    // lists on screen, or the project page keeps counting and listing them.
    workItemListsChanged(slug);
  }

  async deleteProject(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.projectService.deleteProject(slug, projectId);
    track("project_deleted", { project_id: projectId, source });
    const cache = projectCacheFor(slug);
    if (cache) forgetProject(cache.client, cache.scope, projectId);
  }

  /**
   * After a bulk write: both lists are read again, and the detail of every
   * project it may have changed. That is the picked ids for an `included`
   * selection; for `all` / `all_but_excluded` the server resolves the set from
   * filters, so every project of the workspace.
   */
  private markBulkWrite(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
  ): void {
    const cache = projectCacheFor(slug);
    if (!cache) return;
    void invalidateProjectBanks(cache.client, cache.scope);
    if (selection.mode !== "included") {
      void invalidateProjectDetails(cache.client, cache.scope);
      return;
    }
    for (const id of selection.ids ?? []) {
      void cache.client.invalidateQueries({ queryKey: projectDetailKey(cache.scope, id) });
    }
  }

  /**
   * Delete projects selected from the workspace Projects list via the shared
   * selection-contract endpoint (#2594) — the server authorizes per project
   * and deletes only the subset the actor may delete, reporting the rest as
   * `skipped` rather than failing the whole batch. `onProgress` surfaces
   * processed/total while a large ("all"/"all_but_excluded") selection is
   * handed off to a background job; `included` (the only mode the Projects
   * list currently sends) almost always completes inline.
   */
  async bulkDeleteProjects(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    onProgress?: (status: TBulkOperationStatus) => void,
    source: AnalyticsSource = "bulk",
  ): Promise<TBulkDeleteResult> {
    const result = await runBulkDelete(
      () => this.rootStore.projectService.bulkDeleteProjects(slug, selection),
      {
        poll: (token) => this.rootStore.projectService.getBulkOperationStatus(slug, token),
        onProgress,
      },
    );
    const cache = projectCacheFor(slug);
    if (selection.mode === "included" && cache) {
      for (const id of selection.ids ?? []) forgetProject(cache.client, cache.scope, id);
    }
    // The server may have changed any selected row; the store patches none
    // of them (or, for a delete, only an `included` selection).
    this.markBulkWrite(slug, selection);
    track("project_deleted", { count: result.deleted, source });
    return result;
  }

  /**
   * Set the status on projects selected from the workspace Projects list via
   * the shared selection-contract endpoint (#2595) — mirrors
   * `bulkDeleteProjects` (#2594): the server authorizes per project and
   * applies the change only to the subset whose workflow allows the direct
   * transition, reporting the rest as `skipped`. No local optimistic patch —
   * the result doesn't say *which* ids were skipped, so the caller reloads
   * the page to true up rather than risk showing the new status on a
   * skipped row.
   */
  async bulkSetStatus(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    stateId: string,
    onProgress?: (status: TBulkOperationStatus) => void,
    source: AnalyticsSource = "bulk",
  ): Promise<TBulkSetProjectStatusResult | { processed: number; skipped: number }> {
    const result = await runBulkAction<TBulkSetProjectStatusResult>(
      () => this.rootStore.projectService.bulkSetProjectStatus(slug, selection, stateId),
      {
        poll: (token) => this.rootStore.projectService.getBulkOperationStatus(slug, token),
        onProgress,
      },
    );
    // The server may have changed any selected row; the store patches none
    // of them (or, for a delete, only an `included` selection).
    this.markBulkWrite(slug, selection);
    track("project_status_changed", { to: stateId, count: bulkChangedCount(result), source });
    return result;
  }

  /**
   * Archive projects selected from the workspace Projects list via the
   * shared selection-contract endpoint (#2598) — mirrors `bulkSetStatus`
   * (#2595)/`bulkDeleteProjects` (#2594). No local optimistic patch: the
   * result doesn't say which ids were skipped, so the caller reloads the
   * page rather than risk showing an archived state on a skipped row.
   */
  async bulkArchiveProjects(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    onProgress?: (status: TBulkOperationStatus) => void,
    source: AnalyticsSource = "bulk",
  ): Promise<TBulkArchiveProjectsResult | { processed: number; skipped: number }> {
    const result = await runBulkAction<TBulkArchiveProjectsResult>(
      () => this.rootStore.projectService.bulkArchiveProjects(slug, selection),
      {
        poll: (token) => this.rootStore.projectService.getBulkOperationStatus(slug, token),
        onProgress,
      },
    );
    // The server may have changed any selected row; the store patches none
    // of them (or, for a delete, only an `included` selection).
    this.markBulkWrite(slug, selection);
    track("project_archived", { count: bulkChangedCount(result), source });
    return result;
  }

  /**
   * Assign (`assign: true`) or unassign (`false`) one user as project.lead
   * across projects selected from the workspace Projects list via the shared
   * selection-contract endpoint (#2596) — mirrors `bulkArchiveProjects`
   * (#2598)/`bulkSetStatus` (#2595). No local optimistic patch on this store:
   * `BulkLeadsPicker` keeps its own local overlay for instant per-click
   * feedback across a rapid multi-click session, and triggers a page refresh
   * when it closes to true up the shared row data.
   */
  async bulkAssignLead(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    userId: string,
    assign: boolean,
    onProgress?: (status: TBulkOperationStatus) => void,
    source: AnalyticsSource = "bulk",
  ): Promise<TBulkAssignLeadsResult | { processed: number; skipped: number }> {
    const result = await runBulkAction<TBulkAssignLeadsResult>(
      () => this.rootStore.projectService.bulkAssignProjectLeads(slug, selection, userId, assign),
      {
        poll: (token) => this.rootStore.projectService.getBulkOperationStatus(slug, token),
        onProgress,
      },
    );
    // The server may have changed any selected row; the store patches none
    // of them (or, for a delete, only an `included` selection).
    this.markBulkWrite(slug, selection);
    track(assign ? "project_lead_assigned" : "project_lead_unassigned", {
      member_id: userId,
      count: bulkChangedCount(result),
      source,
    });
    return result;
  }

  /** Request cancellation of a long-running bulk operation (delete/archive/
   *  set-status/set-leads) by token — see `cancelBulkOperation` on the
   *  service for the endpoint contract. */
  cancelBulkOperation(slug: string, token: string): Promise<{ token: string; status: string }> {
    return this.rootStore.projectService.cancelBulkOperation(slug, token);
  }

  async duplicateProject(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    const newProject = await this.rootStore.projectV3Service.duplicate(slug, projectId);
    const cache = projectCacheFor(slug);
    if (cache) appendProject(cache.client, cache.scope, newProject);
    track("project_duplicated", {
      project_id: newProject.id,
      duplicated_from_id: projectId,
      source,
    });
    return newProject;
  }
}
